"use client";

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";

import {
  getCurrentOrganizationId,
  setCurrentOrganization,
} from "@/actions/organization";
// The app's guarded router, NOT next/navigation: its refresh() defers to the
// page-lock alert when a page has unsaved work, so a resync or a switch cannot
// silently re-render a route out from under an in-progress import. This
// provider is mounted inside NavigationProvider so the hook resolves.
import { useRouter } from "@/context/NavigationProvider";

/**
 * The effective organization id (the org shown in the switcher), handed down
 * from the authed layout's server render.
 *
 * Why this exists: `useClientApi` needs the id to set the tenant header on
 * every browser fetch, and it used to obtain it by invoking the
 * `getCurrentOrganizationId` SERVER ACTION per request. That made each client
 * fetch cost a `POST /` round trip plus `users/me` + `organizations/:id`
 * upstream, none of it cached — so one dashboard filter change (which fires
 * three fetches) cost three server actions and six upstream calls. Under
 * repeated interaction that was enough to exhaust the dev server's memory.
 *
 * `null` means "not provided" — consumers fall back to the server action, so a
 * component rendered outside this provider still works.
 *
 * This is NOT a cache: the value arrives in the RSC payload, so it is only ever
 * as stale as the layout that produced it. Keeping it fresh is the job of the
 * two mechanisms below — {@link useSwitchOrganization} for the tab that
 * switches, and the visibility re-check for every other tab.
 */
const CurrentOrganizationContext = createContext<string | null>(null);

export function CurrentOrganizationProvider({
  organizationId,
  children,
}: {
  organizationId: string;
  children: ReactNode;
}) {
  const { router } = useRouter();
  const isCheckingRef = useRef(false);

  useEffect(() => {
    /**
     * The org cookie is browser-global but `httpOnly`, so a tab that did not
     * perform the switch cannot see that it changed — and nothing notifies it.
     * Its cached layout would keep feeding the PREVIOUS org id to
     * `useClientApi`'s tenant header while its own server renders already
     * follow the NEW cookie, so the page would show one org and write to
     * another.
     *
     * Checking when the tab is next looked at is enough, because a tab has to
     * be focused before it can be used. `focus` covers side-by-side windows,
     * where `visibilitychange` does not fire; the in-flight guard keeps the
     * two events from issuing the check twice.
     */
    const resync = async () => {
      if (document.visibilityState !== "visible") return;
      if (isCheckingRef.current) return;
      isCheckingRef.current = true;
      try {
        const { data } = await getCurrentOrganizationId();
        // Refetches this tab's RSC payload, re-rendering the layout so the
        // value below follows. Deliberately not a global cache purge: it
        // affects this tab only.
        if (data && data !== organizationId) router.refresh();
      } catch {
        // A failed check just leaves the current value in place; the next
        // focus tries again.
      } finally {
        isCheckingRef.current = false;
      }
    };

    document.addEventListener("visibilitychange", resync);
    window.addEventListener("focus", resync);
    return () => {
      document.removeEventListener("visibilitychange", resync);
      window.removeEventListener("focus", resync);
    };
  }, [organizationId, router]);

  return (
    <CurrentOrganizationContext.Provider value={organizationId}>
      {children}
    </CurrentOrganizationContext.Provider>
  );
}

/** The effective org id, or `null` when rendered outside the provider. */
export function useCurrentOrganizationId(): string | null {
  return useContext(CurrentOrganizationContext);
}

/**
 * Switch the effective organization, then refresh THIS tab so the new id
 * reaches the client.
 *
 * Every switch call site soft-navigates afterwards, and App Router preserves
 * shared layouts across soft navigations — so the authed layout, which is what
 * supplies the org id to `useClientApi`, survives the switch still holding the
 * previous value. `router.refresh()` re-renders it for this tab.
 *
 * Use this rather than calling `setCurrentOrganization` directly: routing the
 * refresh through one hook means a switcher added later cannot forget it.
 *
 * Deliberately NOT done with `revalidatePath("/", "layout")` inside the action:
 * that invalidates the implicit `_N_T_/layout` tag, which is a
 * deployment-wide Data Cache purge rather than a per-user one, so every org
 * switch would drop every cached entry for every user.
 */
export function useSwitchOrganization() {
  const { router } = useRouter();

  return useCallback(
    async (id: string) => {
      const response = await setCurrentOrganization(id);
      if (response.data) router.refresh();
      return response;
    },
    [router],
  );
}
